import type { CatalogField } from '@klothiq/domain';

export interface FieldHelpProps {
  /** Plain-language instructions; never a record value or credential. */
  help?: string;
  helpLabel?: string;
}

interface FieldMetadata {
  label?: string;
  id?: string;
  name?: string;
  type?: string;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  step?: string | number;
  readOnly?: boolean;
}

export const fieldLabel = (metadata: FieldMetadata) =>
  (metadata.label || metadata.name || metadata.id || 'Field')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Shared help describes the control's purpose without reading its entered value. */
export function fieldGuidance(metadata: FieldMetadata): string {
  const label = fieldLabel(metadata);
  const key = label
    .toLowerCase()
    .replace(/[()*₹]/g, '')
    .trim();
  const rules: [RegExp, string][] = [
    [
      /stable field code/,
      'A permanent key for saved values, such as SLEEVE_TYPE. Use uppercase letters, numbers and underscores. Keep an existing code unchanged when renaming its label.',
    ],
    [
      /code prefix/,
      'The short prefix used when generating new product codes. Example: JERSEY or TSHIRT. Use uppercase letters and numbers, up to 12 characters.',
    ],
    [
      /^level$/,
      'Product means one shared value for the whole product, such as Team. Variant means a separate value for each size or colour, such as Size.',
    ],
    [
      /^(input|field type|input type)$/,
      'Choose text, number, Yes/No, a single-choice list, a multiple-choice list or a date. Use text for codes that may contain leading zeros.',
    ],
    [
      /^label$/,
      'The name people see while entering a product. Example: Sleeve type. Changing the label does not rename its stable field code.',
    ],
    [
      /show when/,
      'Show this field only when an earlier field at the same level has a matching value. Choose Always to show it without a condition.',
    ],
    [
      /^equals$/,
      'Enter the exact saved value that makes the conditional field appear. For a choice list, use its option value, not a renamed display label.',
    ],
    [
      /required for new values/,
      'Enable this when a new entry must contain a value. A conditional field is required only while its condition is met. Existing records retain their saved version.',
    ],
    [
      /option(?:\s+\d+)? value|stable value/,
      'The permanent value stored for this choice, such as HALF_SLEEVE. Keep it unchanged after it is used; edit the option label for display wording.',
    ],
    [
      /option(?:\s+\d+)? label|display label/,
      'The readable choice shown to users, such as Half sleeve. Use wording that clearly distinguishes it from other choices.',
    ],
    [
      /maximum characters/,
      'The largest permitted text length. Set a whole number from 1 to 1,000. This is a text limit, not a numeric maximum.',
    ],
    [
      /^(minimum|maximum)$/,
      'An optional numeric boundary. Leave it blank for no boundary. The minimum must not exceed the maximum. Zero is a valid limit.',
    ],
    [
      /load a saved version/,
      'Load a saved draft or published setup for review. Confirm before replacing your unsaved editor changes. Loading a version does not publish it.',
    ],
    [
      /search|find|filter/,
      'Narrow the visible choices or records. Search/filter fields do not edit saved business records. Clear the filter to show the wider list again.',
    ],
    [
      /current password/,
      'Enter your current login password to authorize this change. When managing another person’s login, this is your own password, not their temporary password.',
    ],
    [
      /password/,
      'Use the password requirements shown beside this field. Keep passwords private; never put them in notes, screenshots or messages to support.',
    ],
    [
      /email|e mail/,
      'Enter a complete email address, such as person@example.com. For a personal login, use the address that person will sign in with.',
    ],
    [
      /phone|mobile|whatsapp number/,
      'Enter the customer’s or contact’s phone number, with the correct country format. A customer number is optional on a sale; WhatsApp sharing needs a valid number.',
    ],
    [
      /subcategory|collection/,
      'An optional grouping within the product category, such as Football Jerseys. Only the collections allowed by this category setup are offered for new products.',
    ],
    [
      /product category|^category$/,
      'Choose the product family, such as Jersey, T-shirt, Shirt or Helmet. Its published setup determines which product and variant fields appear.',
    ],
    [
      /category name/,
      'Use a clear, distinct name. A product category defines a product family; an expense category groups spending. Check existing names before adding another.',
    ],
    [
      /product code/,
      'A unique identifier for the product. Optional Jersey entry generates it after a name is entered and lets you edit it, including saved records. Other category workflows may generate blank new codes when saved.',
    ],
    [
      /^sku$/,
      'A unique identifier for one variant. Optional Jersey entry displays it after the name and code are ready; you can edit it at any time. Editing a saved SKU keeps its variant ID and stock history.',
    ],
    [
      /product name/,
      'Use a recognisable product name, such as Real Madrid Home Jersey. Put size and colour in the variant fields.',
    ],
    [
      /target audience/,
      'Optionally classify who the product is intended for, using the choices configured by your administrator.',
    ],
    [
      /age group/,
      'Optionally choose the age range this product is intended for. This classification does not add stock.',
    ],
    [
      /size set/,
      'Choose a prepared group of sizes to add variant rows. Review each row’s colour, cost, price and optional opening stock before saving.',
    ],
    [
      /^size$/,
      'Enter the size for this variant, such as M, XL or a helmet size. Products without sizes can use Standard.',
    ],
    [
      /colour|color/,
      'Enter the variant’s colour, such as Black or Black/Blue. Use consistent wording for matching variants.',
    ],
    [
      /low.stock/,
      'The stock threshold used to identify a low-stock variant. Enter a whole number. This changes the alert threshold, not the stock quantity.',
    ],
    [
      /add stock now|opening stock|initial stock/,
      'Optional stock for a newly created variant. Enter a whole quantity; zero adds none. Saving posts stock, so do not enter units already recorded in Inventory.',
    ],
    [
      /sale price|selling price|default price|unit price/,
      'The price for one unit in rupees. Use at most two decimal places. Review any discount separately.',
    ],
    [
      /cost|unit cost/,
      'The cost of one unit in rupees. This is different from the sale price. Use at most two decimal places.',
    ],
    [
      /discount/,
      'Enter the discount in the format shown by this form. A field labelled with ₹ is an amount; a field labelled with % is a percentage. Review the calculated total before saving.',
    ],
    [
      /quantity|^qty$/,
      'Enter the number of units as a whole number. Review the product or variant against this quantity before saving.',
    ],
    [
      /seller|sold by/,
      'Choose the person responsible for the sale. Seller attribution is separate from the login that creates or edits the record.',
    ],
    [
      /supplier/,
      'Choose the supplier the purchase relates to. Supplier details describe the source of the purchase; they do not receive stock automatically.',
    ],
    [
      /assignee|assign to|assigned to/,
      'Choose the person responsible for this task. Assignment notifications are sent inside the web app.',
    ],
    [
      /priority/,
      'Choose how urgently this work should be handled. Priority does not automatically change its due date or completion status.',
    ],
    [
      /progress/,
      'Record the work completed using the range shown by the form. Add a useful update so others understand what changed.',
    ],
    [
      /reference|invoice number/,
      'Enter the external payment or supplier reference, if available. Reuse the actual reference so records can be reconciled.',
    ],
    [
      /payment method|^method$/,
      'Choose how the money was paid, such as cash, UPI or bank transfer. Select the matching account separately.',
    ],
    [
      /account/,
      'Choose the cash or bank account used for this transaction. A recorded payment affects that account’s balance; review the amount and date first.',
    ],
    [
      /amount|delivery charge|total/,
      'Enter the rupee amount for this field, using at most two decimal places. Review the form’s summary and payment options before saving.',
    ],
    [
      /status/,
      'Choose the stage that matches the saved record. Delivery status and payment status are separate. Review any stock or payment effects described on this screen.',
    ],
    [
      /date|deadline/,
      'Choose a calendar date or use Enter date in the date picker. Use the date the activity actually occurred; check any displayed date limits.',
    ],
    [
      /reason/,
      'Briefly explain why this change is needed. Use enough detail for another authorised user to understand the correction later.',
    ],
    [
      /address/,
      'Enter the delivery or contact address, including useful location details. Check it before including it in a customer message.',
    ],
    [
      /notes|description/,
      'Add useful details for the people using this record. Do not include passwords or other login secrets.',
    ],
    [
      /sort order|display order/,
      'Controls the order in which these entries appear. Smaller numbers appear first where this ordering is supported.',
    ],
    [
      /keyword/,
      'Enter matching words separated by commas. These help classify imported rows; review the suggested match before committing an import.',
    ],
  ];
  let help = rules.find(([pattern]) => pattern.test(key))?.[1];
  if (!help) {
    if (metadata.type === 'checkbox' || metadata.type === 'radio')
      help =
        'Select this option to enable it. Review the neighbouring label and any confirmation before saving.';
    else if (metadata.type === 'select')
      help =
        'Choose the option that matches this record. Use arrow keys and Enter with a keyboard; an empty or None option leaves an optional choice unset.';
    else if (metadata.type === 'number')
      help =
        'Enter a number in the units shown by the label. Follow the limits and step size displayed for this field.';
    else if (metadata.type === 'file')
      help =
        'Choose the file requested on this screen. Review the import preview and any validation messages before committing data.';
    else
      help = `Enter ${label === 'Field' ? 'the requested information' : label.replace(/\s*\*$/, '').toLowerCase()} clearly. Check the field label and validation messages before saving.`;
  }
  if (metadata.readOnly) help += ' This field is read-only on this screen.';
  const limits: string[] = [];
  if (metadata.min !== undefined) limits.push(`Minimum: ${metadata.min}.`);
  if (metadata.max !== undefined) limits.push(`Maximum: ${metadata.max}.`);
  if (metadata.maxLength !== undefined && metadata.maxLength >= 0)
    limits.push(`Up to ${metadata.maxLength} characters.`);
  if (metadata.step !== undefined && metadata.step !== 'any')
    limits.push(`Number step: ${metadata.step}.`);
  return [help, ...limits].join(' ');
}

export function fieldHelpAttributes(metadata: FieldMetadata, help?: string) {
  const description = help ?? fieldGuidance(metadata);
  return {
    'data-field-label': fieldLabel(metadata),
    'data-field-help': description,
    'aria-description': description,
  };
}

export function catalogFieldGuidance(field: CatalogField, fields: CatalogField[]): string {
  const examples: Record<string, string> = {
    TEAM: 'The club, team or country represented by this product. Example: Real Madrid.',
    EDITION: 'The product edition, such as Home, Away, Third or Training.',
    SLEEVE_TYPE: 'Choose the sleeve style, such as Half Sleeve, Full Sleeve or Sleeveless.',
    COLLAR_TYPE: 'Describe the collar style, such as Polo or Round neck.',
    NECKLINE: 'Describe the neckline, such as Crew neck or V-neck.',
    PLAYER_NAME:
      'The printed player name, if this item has one. Leave an optional value blank for an unprinted item.',
    PLAYER_NUMBER: 'The printed player number. Keep a leading zero when it is part of the print.',
    MATERIAL: 'The fabric or material, such as Polyester, Cotton or a material blend.',
    FIT: 'The cut of the garment, such as Regular Fit, Slim Fit or Oversized.',
    BRAND: 'The manufacturer or brand printed on the item.',
    STYLE: 'Choose the product style from the configured choices.',
    CERTIFICATION:
      'Enter the certification stated on the product or its documentation. Do not infer a certification.',
  };
  const usage = examples[field.code] ?? fieldGuidance({ label: field.label, type: field.type });
  const typeHelp: Record<CatalogField['type'], string> = {
    text: 'Enter text.',
    number: 'Enter a number.',
    boolean: 'Choose Yes or No; leave Choose for an optional unanswered value.',
    select: 'Choose one option.',
    multiselect: 'Select all applicable options.',
    date: 'Choose a calendar date.',
  };
  const condition = field.when
    ? ` Shown when ${fields.find((f) => f.code === field.when!.field)?.label ?? field.when.field} equals ${String(field.when.equals)}.`
    : '';
  const limits = [
    field.min !== undefined ? `Minimum: ${field.min}.` : '',
    field.max !== undefined ? `Maximum: ${field.max}.` : '',
    field.maxLength !== undefined ? `Up to ${field.maxLength} characters.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return `${usage} ${typeHelp[field.type]} ${field.required ? 'Required when shown for a new value.' : 'Optional unless another rule requires it.'} ${field.level === 'variant' ? 'Entered separately for each variant.' : 'Shared by the whole product.'}${condition}${limits ? ` ${limits}` : ''}`;
}

export function screenGuidance(path: string): { title: string; steps: string[] } {
  if (path.startsWith('/master-config/catalog'))
    return {
      title: 'Set up a product category',
      steps: [
        'Create the product category in Master Configuration first, then choose it here. Category means a family such as Jersey, T-shirt, Shirt or Helmet.',
        'Set a code prefix. Add readable field labels and stable codes. Choose Product for shared details and Variant for details that differ by size or colour.',
        'Choose a field type, available options, required rules and optional limits. A conditional field can depend only on an earlier field at the same level.',
        'Choose the allowed subcategories/collections. An empty selection offers None only for new products.',
        'Preview the fields, save a draft, then publish after reviewing. A draft alone does not enable the setup for new products. Existing products retain their saved setup until explicitly updated.',
      ],
    };
  if (path.startsWith('/master-config'))
    return {
      title: 'Manage reusable choices',
      steps: [
        'Product Categories define product families. Subcategories/collections classify products within a family. Category setup controls the fields used for new products.',
        'Use clear names and stable codes. Size sets are reusable lists of sizes; audience and age groups are optional classifications.',
        'Staff entries identify people; creating a staff entry does not create a personal login. Users & Access manages login accounts and permissions.',
        'Expense categories group spending. Keywords help match imported rows; review each match. Deactivate an unused choice when its history must remain available.',
      ],
    };
  if (path.startsWith('/products/import'))
    return {
      title: 'Import products',
      steps: [
        'Choose the category and published setup, map the spreadsheet columns and review validation.',
        'Review new versus matched products and variants. Opening stock must not repeat stock already recorded.',
        'Commit only the intended groups and review individual success or error results. Correct a failed group before retrying.',
      ],
    };
  if (path.startsWith('/products'))
    return {
      title: 'Enter a product',
      steps: [
        'Choose its product category first. The published setup determines the relevant fields.',
        'Enter the product name and optional classifications. New blank product codes and SKUs are generated when saved; existing identifiers remain attached to their records.',
        'For a category with optional jersey entry, every jersey detail can be skipped. Type the full name directly or choose Auto for a consistent suggestion. Name, new code and each new SKU have independent Manual/Auto options; manual edits are kept.',
        'Add the sizes/colours you sell as variants. Review each variant’s cost, sale price and low-stock threshold.',
        'Optional Add stock now posts opening stock for new variants. Use Inventory for later stock entries. Editing product details does not repost stock.',
      ],
    };
  if (path.startsWith('/purchases'))
    return {
      title: 'Record a purchase',
      steps: [
        'New record-only purchases can contain an amount alone, with optional descriptions or catalog references.',
        'Record delivery status and date separately from payment. Record-only purchase entry does not add products or stock.',
        'Choose an account debit only when recording a real payment. Skipping payment does not mean Paid; unpaid supplier tracking is a separate choice.',
        'Older Legacy stock orders keep their stock-receiving workflow. Read the mode label before receiving. Delete/restore eligible mistakes; archive records whose genuine history must remain.',
      ],
    };
  if (path.startsWith('/print-templates'))
    return {
      title: 'Print blank branded templates',
      steps: [
        'Choose delivery address labels or greeting cards. No saved sale or customer is needed.',
        'Enter a whole number of copies from 1 to 100. Address labels fit three or four per A4 sheet; greeting cards fit two to keep their QR codes readable.',
        'For labels, enter our sender name, full postal address and optional phone. Super Admin can Save address to store the shared default in the database. Reload saved address replaces your current draft; restoring brand details only changes the draft until saved. Turn off Print sender details to leave FROM blank as well as TO.',
        'Review the preview, then use A4 portrait at 100% scale without browser headers/footers. Cut between units and write in the missing details.',
      ],
    };
  if (path.startsWith('/sales'))
    return {
      title: 'Enter and update a sale',
      steps: [
        'Enter the customer and seller, then add variants, quantities, sale prices and discounts. Customer phone is optional; WhatsApp requires a valid number.',
        'Review the invoice total and select the correct delivery stage. Delivery and payment are separate statuses.',
        'Record a new collection only when more money is received. Correct an existing payment through its Edit action to avoid a duplicate.',
        'Review the saved details before sharing a WhatsApp message or PDF. A share preview does not send a message until you complete the sharing action.',
      ],
    };
  if (path.startsWith('/inventory'))
    return {
      title: 'Manage stock',
      steps: [
        'Choose the exact product variant and review its available and reserved quantities.',
        'Add stock only for units that have not already been recorded. Enter quantity, unit cost and a useful reason.',
        'Use the appropriate adjustment/receiving action for corrections or legacy orders. Stock entry does not itself record a bank payment.',
      ],
    };
  if (path.startsWith('/settings/users'))
    return {
      title: 'Manage personal logins',
      steps: [
        'Choose the person’s details, role and any staff link. Staff identity and login access are separate.',
        'Select only the screens and actions required for this person. Some permissions require others. Selecting all privileges requires confirmation.',
        'Use a temporary password and authorize the change with your current Super Admin password. Share credentials privately.',
      ],
    };
  if (path.startsWith('/plans') || path.startsWith('/my-tasks'))
    return {
      title: 'Plan and track work',
      steps: [
        'Create a clear plan or task title, add useful details and choose the responsible person.',
        'Set priority and dates, then break work into checklist steps where available.',
        'Update progress and status as work happens. Explain deadline changes or blockers. Assignment notifications stay inside the app.',
      ],
    };
  if (path.startsWith('/accounts') || path.startsWith('/payments'))
    return {
      title: 'Record account activity',
      steps: [
        'Choose the intended account and transaction type. Review whether the action increases or decreases its balance.',
        'Enter the amount, actual transaction date, method and external reference, if available.',
        'Do not record a payment again if it already exists against a sale or purchase. Use its correction flow and review account balances afterwards.',
      ],
    };
  if (path.startsWith('/customers') || path.startsWith('/suppliers'))
    return {
      title: 'Manage contacts',
      steps: [
        'Use a clear contact name and check existing contacts before creating a duplicate.',
        'Add accurate contact and address details where available. Keep login passwords out of contact notes.',
        'Review linked records before changing or deactivating a contact.',
      ],
    };
  if (path.startsWith('/deliveries'))
    return {
      title: 'Pack and follow deliveries',
      steps: [
        'Choose Courier or Local delivery in a Sale. Classify older sales from Needs delivery method.',
        'Check items and the recipient address, mark Packed, then enter individual courier tracking numbers when dispatching.',
        'Select up to 100 shipments for bulk status changes or documents. Failed rows stay selected for review.',
        'Preview the saved delivery update before opening each WhatsApp chat. You send the message inside WhatsApp.',
        'Use the Sale return workflow for customer returns; a courier return-to-origin flag does not restore stock or refund money.',
      ],
    };
  if (path.startsWith('/reports') || path === '/' || path.includes('sales'))
    return {
      title: 'Read totals and reports',
      steps: [
        'Choose the date range and filters, then apply them where the screen offers Apply.',
        'Period activity follows the selected dates. Figures labelled Current describe the current balance or stock position.',
        'Invoice dates, collection dates and refund dates can differ. Check the basis of a metric before comparing totals or exporting.',
      ],
    };
  if (path.startsWith('/recycle-bin'))
    return {
      title: 'Restore or remove records',
      steps: [
        'Choose the record category and review the saved details.',
        'Restore an eligible record when it is needed again. Review the preview and confirmation before deletion.',
        'Protected financial or stock history may prevent permanent deletion. Bulk actions show the eligible and protected outcomes.',
      ],
    };
  return {
    title: 'Use this screen',
    steps: [
      'Review the labels and instructions for the action you are completing.',
      'Fields marked with an asterisk are required. Read any validation messages before saving.',
      'Review the confirmation and resulting status. If a save result is uncertain, use the available recovery action before submitting again.',
    ],
  };
}
